export const orderTypes = [
  {
    slug: "market-orders",
    name: "Market Order",
    short: "Market",
    icon: "ri-rocket-2-line",
    category: "Instant execution",
    tagline: "Get in or out now, at the best price available.",
    summary:
      "A market order buys or sells immediately at the best available price. In any normally liquid market it is filled almost instantly — it buys you certainty of a fill, not an exact price.",
    intro:
      "The most direct instruction you can give a broker: fill me right now, at whatever the market offers. Nothing is priced, nothing is negotiated — you are simply asking to act.",
    oneLiner: "Reach for it when being in the trade now matters more than the exact price.",
    definition:
      "A market order is an instruction to buy or sell a currency pair immediately at the best available price. Because it is filled against the orders already sitting in the market, execution is almost instant — but the exact price is not guaranteed. You trade a little price certainty for speed and the certainty of a fill.",
    keyPoints: [
      {
        icon: "ri-speed-up-line",
        title: "You are never left out",
        text: "No price condition stands between your idea and the market — the moment you decide, the trade is on. That immediacy, not a better price, is what you are buying.",
      },
      {
        icon: "ri-price-tag-3-line",
        title: "Expect the fill to land off",
        text: "The number you click and the number you get are almost never the same — check the confirmation, not the quote on screen, and read the trade from where it actually opened.",
      },
      {
        icon: "ri-equalizer-line",
        title: "You pay for the guarantee",
        text: "The spread is the toll for trading on demand — and it is charged whether the moment deserved it or not. Pay it deliberately, on the entries where acting now genuinely matters.",
      },
    ],
    howItWorks: [
      "When you submit a market order, your broker looks at the top of the order book — the best bid and the best ask — and fills you there. Buy orders are filled at the ask, sell orders at the bid.",
      "In liquid pairs such as EUR/USD during active sessions, the depth of the book is huge, so even large orders fill close to the quoted price. In thinner conditions — late Friday, holidays, or around major news — the book thins out and your fill can slip.",
      "Because there is no price condition attached, a market order has no waiting state: the instant it reaches the book, it trades. Nothing in it can pause, expire, or be declined — the trade simply happens.",
    ],
    example: {
      scenario:
        "You want to buy 1 standard lot (100,000 units) of EUR/USD right now, in the London session.",
      table: [
        { label: "Live bid", value: "1.08420" },
        { label: "Live ask", value: "1.08432" },
        { label: "Spread", value: "1.2 pips" },
        { label: "Your order", value: "Buy 1 lot at market" },
        { label: "Fill price", value: "1.08432 (the ask)" },
        { label: "Spread cost", value: "≈ $12" },
      ],
      note: "Nothing was negotiated — you simply took what the book offered. In this deep session that cost a single clean spread; in a thin one, the same click could cost far more.",
    },
    pros: [
      "Gets you in or out the moment your idea forms — nothing to set, nothing to wait on",
      "The order most certain to trade; it wins when missing the move hurts more than a poorer price",
      "No levels or conditions to reason about — the fastest order to act on in live markets",
    ],
    cons: [
      "Your worst fills arrive at your worst moments — right when the market is moving fastest",
      "Every entry starts a fraction behind the screen price, a drag that compounds across many trades",
      "No price ceiling at all: in a gap, the market picks your fill, not you",
    ],
    misuses: [
      {
        title: "Firing off a market order into a news release",
        tag: "Slippage",
        why: "Spreads can widen several times over in the seconds around a release, and the top of the book thins out. The 'instant' fill you expect can land far from the price you clicked.",
        instead:
          "Let the spread settle first, or use a limit order and accept that you may miss the initial spike.",
        example:
          "On a US non-farm payrolls print, EUR/USD's spread has jumped from about 1.2 pips to 15+ in under a second — a 1-lot market buy filled roughly 14 pips from the price on screen.",
      },
      {
        title: "Sizing as if the price is guaranteed",
        tag: "Sizing",
        why: "A market order guarantees a fill, not a price. New traders size a position off the quoted price, get a slightly worse fill, and find their risk is bigger than they planned.",
        instead:
          "Assume a small adverse fill, and size the trade so a bit of slippage still fits your risk.",
        example:
          "A planned 1% risk on a 30-pip stop quietly becomes 1.1% after a 3-pip slip — tiny once, but it quietly compounds across every rushed entry.",
      },
      {
        title: "Reaching for market orders in a dead session",
        tag: "Liquidity",
        why: "Late Friday, holidays and the daily rollover leave the book thin. Even a normal size moves price further there than it would in the London or New York session.",
        instead:
          "Trade when liquidity is deepest, and keep size modest whenever it isn't.",
        example:
          "The same 1-lot order that costs around 1.2 pips in the London session can cost 4–6 pips into the Friday close — several times the entry cost for identical size.",
      },
    ],
    whenToUse: [
      "You need to enter or exit immediately",
      "You are trading a market deep enough that the spread stays a small, predictable cost",
      "You have already accepted a small amount of price uncertainty",
    ],
    whenNotToUse: [
      "You need a specific price, not just any fill — if the exact number matters, a limit order trades time for that control",
      "You are trading a size the book can't quietly absorb — an order that big moves the price as it fills, so the impact is self-inflicted no matter which session you choose",
      "Nothing is forcing the entry — if the trade can wait, patience costs you nothing and a limit may fill you better",
    ],
    related: ["limit-orders", "stop-orders"],
    cta: {
      title: "Where the click and the fill come apart",
      description:
        "A market order is certain to trade — the price is not. Follow it from submission through routing to the book, and see exactly where the fill drifts from the number on your screen.",
      buttonLabel: "Follow the order to its fill",
    },
    faqs: [
      {
        q: "Does a market order guarantee a price?",
        a: "No. A market order guarantees a fill, not a price. You are filled at the best price available at that moment, which can differ from the quote you clicked on — that difference is slippage.",
      },
      {
        q: "Can a market order go unfilled?",
        a: "In any normally liquid market, practically never. A market order simply takes the best price on the other side of the book. Only in extreme conditions with no liquidity at all could a fill be delayed.",
      },
      {
        q: "Is a market order a bad idea during major news?",
        a: "It is risky. Spreads widen and liquidity thins around a release, so slippage can be large. If you trade the news, expect a worse fill — or use a limit order and accept that it may not fill at all.",
      },
    ],
  },
  {
    slug: "limit-orders",
    name: "Limit Order",
    short: "Limit",
    icon: "ri-filter-3-line",
    category: "Price control",
    tagline: "Name your price. Only fill if the market comes to you.",
    summary:
      "A limit order only executes at your specified price or better. It gives you full price control but no guarantee of a fill.",
    intro:
      "A limit order flips the power dynamic. Instead of chasing the market, you set the price you are willing to trade at and wait for the market to come to you.",
    oneLiner: "Reach for it when the price matters more than whether you get filled at all.",
    definition:
      "A limit order is an instruction to buy below the current market price or sell above it. The order sits in the book until the market reaches your price, and it will only ever fill at that price or better. If the market never reaches your level, nothing is forced on you — the trade simply never happens.",
    keyPoints: [
      {
        icon: "ri-focus-3-line",
        title: "Your price, or nothing",
        text: "A limit can never fill worse than your level, so the risk you set at the start is the risk you carry — the number on your plan stays the number on the trade.",
      },
      {
        icon: "ri-timer-line",
        title: "The cost is the move you miss",
        text: "There is no penalty for an order that never fills — the price is merely one you did not pay. Decide up front that you can live without the trade; that is what lets the level stand instead of being chased.",
      },
      {
        icon: "ri-arrow-down-double-line",
        title: "Waiting has to be worth it",
        text: "You are trading time for a better price, so pick a level that earns the wait — one the market actually has to travel to, not a hair's breadth from where price already sits.",
      },
    ],
    howItWorks: [
      "A buy limit is entered below the current ask and fills when the ask drops to your price. A sell limit is entered above the current bid and fills when the bid rises to your price.",
      "Because you are providing liquidity instead of taking it, many brokers let you trade with a smaller or even zero spread markup — you often get a better effective price than a market order would have given.",
      "A limit the price turns away from does not chase the market — it simply rests in the book, untouched, until the market returns to your level or the order expires.",
    ],
    example: {
      scenario:
        "EUR/USD is trading at 1.08500 and you only want to buy if it dips to your target level.",
      table: [
        { label: "Current price", value: "1.08500" },
        { label: "Your limit price", value: "1.08200" },
        { label: "Order type", value: "Buy Limit" },
        { label: "Condition", value: "Wait until ask ≤ 1.08200" },
        { label: "Fill", value: "1.08200 or better" },
        { label: "If never reached", value: "Order stays pending" },
      ],
      note: "The cost here is invisible — nothing is lost while you wait, but you are also not in the trade. Patience is the price of a perfect entry.",
    },
    pros: [
      "You name the price and never chase — the market has to come to you",
      "Your risk maths holds: a fill can never be worse than the level you chose",
      "The same order doubles as a take-profit, banking winners while you are away",
    ],
    cons: [
      "A level the market never reaches earns you nothing — no fill, no position, and the move you read correctly plays out with you on the sidelines",
      "The better price is bought with time — the level only fills when the market returns to it, and every session spent waiting is one you were flat",
      "A resting limit fires the instant price arrives — including when it arrives in a news spike",
    ],
    misuses: [
      {
        title: "Placing a limit and assuming it will fill",
        tag: "No fill",
        why: "A limit sits until the market comes to you. If price turns just before your level, the move happens without you — and it is easy to confuse 'no fill' with 'no harm'.",
        instead:
          "Set an alert at your level and have a plan for when price reacts without you in the trade.",
        example:
          "Price dipped to 1.08205 — barely half a pip above a 1.08200 limit — then reversed 80 pips. The level was right; the order simply never filled.",
      },
      {
        title: "Leaving resting limits open through news",
        tag: "Timing",
        why: "A resting limit does not care why price arrives — it fires the instant the market trades through it. Around a release, that is exactly when a fast, one-way move is already underway, so you can be filled simply because price is steamrolling past your level.",
        instead:
          "Cancel stale limits before scheduled releases and re-place them once the market settles.",
        example:
          "A buy limit parked 30 pips below the market during a CPI print filled as price crashed straight through it — then kept falling another 60 pips before it paused, leaving the position deep in the red.",
      },
      {
        title: "Capping a winner with a target set too tight",
        tag: "Early exit",
        why: "A take-profit placed too close to entry banks a small gain and exits right before the real move. The trade was right; the target was emotional, not structural.",
        instead:
          "Base the target on structure or a measured move, not on how much profit feels comfortable.",
        example:
          "You bank +20 pips at your take-profit — then the pair runs another +120 without you. The target captured a seventh of the move the setup implied.",
      },
    ],
    whenToUse: [
      "You have a defined support or resistance level",
      "You want to buy a dip or sell a rally at a specific price",
      "You want to bank a winner automatically while you are away from the screen",
    ],
    whenNotToUse: [
      "Missing the move is the bigger risk — if a pullback never comes, you are left watching the trade run without you",
      "You are chasing a breakout — price leaves the level and does not look back, so the order simply never fills",
      "You need to be out by a fixed time — before an event or at the close — where a fill that may not arrive is worse than a slightly worse price",
    ],
    related: ["market-orders", "stop-limit-orders"],
    cta: {
      title: "Your price is safe. The fill is not.",
      description:
        "A limit can never fill worse than your level, so the whole risk moves to whether it fills at all. See what a thin book and a fast move do to an order resting in it.",
      buttonLabel: "See why a limit goes unfilled",
    },
    faqs: [
      {
        q: "What is the difference between a limit order and a market order?",
        a: "A market order fills now at the best available price; a limit order only fills at your chosen price or better, and may never fill at all. One buys you certainty of execution, the other certainty of price.",
      },
      {
        q: "Do I lose money if my limit order never fills?",
        a: "No. An unfilled limit order simply does nothing — it stays pending until your level is reached or it expires. You miss the trade, but you are not in a position and lose nothing beyond the opportunity.",
      },
      {
        q: "Does a limit order always avoid slippage?",
        a: "For the fill itself, yes — a limit order never fills worse than your price. The trade-off is fill risk: in a fast market price can jump past your level and leave the order untouched.",
      },
    ],
  },
  {
    slug: "stop-orders",
    name: "Stop Order",
    short: "Stop",
    icon: "ri-alarm-warning-line",
    category: "Trigger automation",
    tagline: "A line in the sand that turns into action automatically.",
    summary:
      "A stop order waits until price reaches a trigger level, then becomes a market order. Used for stop-losses and breakout entries.",
    intro:
      "Stop orders are your risk managers. They sit quietly until price hits a level you chose, then they act — usually to cut a losing trade before it gets worse.",
    oneLiner: "Reach for it when being reliably out matters more than the exact price you leave at.",
    definition:
      "A stop order is a conditional order. You set a trigger price; when the market reaches it, the order activates and is sent to the market as a market order. A stop-loss is the classic use — but stop orders are also used to enter breakouts when price clears a key level.",
    keyPoints: [
      {
        icon: "ri-stop-circle-line",
        title: "Runs while you don't",
        text: "Set once and it acts on its own, so a loss is capped and a breakout is caught even when you are nowhere near the screen.",
      },
      {
        icon: "ri-shield-check-line",
        title: "The level defines the trade",
        text: "You can't size a position until you know where it ends — placing the stop is what turns an idea into a trade with a fixed risk rather than a hope with a number attached.",
      },
      {
        icon: "ri-flashlight-line",
        title: "A floor, not a fixed price",
        text: "It puts a floor under the loss, not a fixed price on the exit: in calm conditions you are filled close to your level, in violence you are not, and both are outcomes to plan for rather than surprises.",
      },
    ],
    howItWorks: [
      "For a long position, you place a sell stop below your entry at the price where your idea is proven wrong. If the bid falls to that level, the stop fires and closes the trade.",
      "For a breakout entry, a buy stop sits just above resistance. When price breaks through, the stop fires and you are positioned with momentum.",
      "Because the trigger turns into a market order, the fill is decided by the prices actually resting in the market — not by the level you chose. That is exactly why stop-limit orders exist.",
    ],
    example: {
      scenario:
        "You are long EUR/USD at 1.08500 and want to cap the damage if it falls.",
      table: [
        { label: "Entry", value: "Buy 1 lot at 1.08500" },
        { label: "Stop-loss level", value: "1.08100" },
        { label: "Order type", value: "Sell Stop" },
        { label: "Trigger", value: "Bid touches 1.08100" },
        { label: "Becomes", value: "Market order" },
        { label: "Risk", value: "40 pips ≈ $400" },
      ],
      note: "The 40 pips above is the risk you designed, not the fill you are promised — plan expecting the exit to land near your level, not exactly on it.",
    },
    pros: [
      "Runs your risk plan around the clock, so a loss is capped even when you are not watching",
      "Takes emotion out of the exit — the decision was made calmly, before the pressure",
      "Caps the downside while leaving the upside open — a stop sets where the risk ends, not where the profit does",
    ],
    cons: [
      "A gap can blow straight through the trigger, so on the worst days the exit lands far beyond the line you drew",
      "Set it too close and everyday noise does the damage, exiting a trade that was about to work",
      "In fast news, a widening spread can trigger the stop and slip it in the same breath",
    ],
    misuses: [
      {
        title: "Placing the stop too close to entry",
        tag: "Noise stop-out",
        why: "Ordinary noise — the everyday up-and-down of price — can reach a tight stop, take you out, and then let the market run exactly where you expected, without you.",
        instead:
          "Place the stop beyond a level where your idea is genuinely wrong, not at an arbitrary small distance.",
        example:
          "A 10-pip stop on EUR/USD during the London open gets brushed by routine noise — you're out, and price then turns and reaches your original target without you.",
      },
      {
        title: "Parking stops on the obvious round numbers",
        tag: "Stop hunting",
        why: "Everyone's stop-losses cluster at the same round levels and recent lows. That makes them an easy target for a quick sweep before price resumes its path.",
        instead:
          "Offset the stop a little past the obvious level so you are not standing in the crowd.",
        example:
          "Stops clustered at 1.08000 get swept down to 1.07980 in a couple of minutes, then price closes right back above the level it never truly broke.",
      },
      {
        title: "Believing the stop locks in your exit price",
        tag: "Slippage",
        why: "It is easy to read the trigger as a promise. But a stop only starts the exit — after that the market sets the price, and in a fast move it can be one you would never have chosen.",
        instead:
          "Accept that the exit can slip, or use a stop-limit when price certainty matters more than the fill.",
        example:
          "A weekend gap on a major pair can jump straight past a stop, filling 40–60 pips beyond the trigger — the risk was capped, but at a price you never chose.",
      },
    ],
    whenToUse: [
      "You are holding a leveraged position you cannot sit and watch",
      "You want to enter a breakout once price clears a key level",
      "You want to lock in gains with a trailing stop as a trade runs",
    ],
    whenNotToUse: [
      "A fill beyond your level is unacceptable — on trigger a stop becomes a market order, so if the exit price matters, a stop-limit caps it instead",
      "You can't yet name the price that proves the idea wrong — a stop placed before that level exists is a line with nothing to defend",
      "Your reason for the trade is still intact — a stop set inside ordinary noise exits you on randomness, not on the idea being wrong",
    ],
    related: ["stop-limit-orders", "market-orders"],
    cta: {
      title: "The trigger fires — the price does not follow",
      description:
        "A stop commits you to leaving, not to the price you leave at. See what the market hands back when your level is reached in a fast, thin moment — and how far a fill can land past the line you drew.",
      buttonLabel: "See what a triggered exit costs",
    },
    faqs: [
      {
        q: "Why do traders use stop orders?",
        a: "Mainly to control risk. A stop-loss automatically closes a losing position at a level you chose, so the loss is capped without you watching the screen. Stops are also used to enter breakouts.",
      },
      {
        q: "Can a stop order fill past my level?",
        a: "Yes. Once triggered, a stop becomes a market order and fills at whatever price is available. In a gap or a violent spike that can be well past your trigger — that distance is slippage.",
      },
      {
        q: "What is the difference between a stop-loss and a stop order?",
        a: "Same mechanism, different intent. A stop order is any order that activates at a trigger level; a stop-loss is a stop order used specifically to close a losing position and cap risk.",
      },
    ],
  },
  {
    slug: "stop-limit-orders",
    name: "Stop-Limit Order",
    short: "Stop-Limit",
    icon: "ri-shield-keyhole-line",
    category: "Trigger and price control",
    tagline: "Trigger like a stop, but never fill worse than your limit.",
    summary:
      "A stop-limit order combines a stop trigger with a limit price. You get price protection — but no guarantee of a fill.",
    intro:
      "A stop-limit is the careful trader's stop. It decides when to act and how much price it is willing to accept, refusing any fill outside that range.",
    oneLiner: "Reach for it when a bad fill is worse than no fill at all.",
    definition:
      "A stop-limit order has two prices: a stop (trigger) and a limit. When price reaches the stop, the order wakes up and places a limit order at your chosen limit price. It will only fill within that range — so you never suffer a bad fill, but you also might not get filled at all if the market moves too fast.",
    keyPoints: [
      {
        icon: "ri-git-commit-line",
        title: "You name the worst price",
        text: "The furthest price you will accept is a number you fix in advance, so a violent print cannot drag you out beyond it. The market decides whether the level trades — you decide how bad the fill may get.",
      },
      {
        icon: "ri-lock-line",
        title: "Two prices, one order",
        text: "Trigger and limit ride in a single order, so they can never be handled apart — there is no half of the exit left waiting while the other fires.",
      },
      {
        icon: "ri-error-warning-line",
        title: "No fill is the trade you make",
        text: "The price you refuse is the fill you may never get — be willing to sit the whole move out. If being out for certain is the priority, this is not the order for you.",
      },
    ],
    howItWorks: [
      "Picture a long position with a stop at 1.08100. A plain stop would fire a market order there, whatever the price. A stop-limit adds a limit of 1.08050: once triggered, it places a limit that will not sell below 1.08050.",
      "This caps the worst price you accept. Note that the two prices do different jobs: the stop decides when the order wakes up, the limit marks the furthest price you will take — and the market has to trade between them for anything to happen.",
      "Once triggered, the order hands over to a normal limit that rests at your price for the session — it can still fill on a later touch, or be cancelled when the day is done.",
    ],
    example: {
      scenario:
        "You want protection at 1.08100 but refuse to be filled below 1.08050.",
      table: [
        { label: "Current price", value: "1.08500" },
        { label: "Stop (trigger)", value: "1.08100" },
        { label: "Limit price", value: "1.08050" },
        { label: "On trigger", value: "Places a limit order" },
        { label: "Fill range", value: "1.08100 – 1.08050" },
        { label: "Big risk", value: "May not fill in a fast crash" },
      ],
      note: "Wide enough to survive a fast move, tight enough to still be a stop — that balance is the whole craft. Set the band around the trade's volatility, not around a round number.",
    },
    pros: [
      "A hard ceiling on the worst fill — you keep control of the exit price, not the market",
      "One order carries both decisions: when to act, and the price you refuse to go past",
      "The right fit when price discipline matters more than the certainty of getting out",
    ],
    cons: [
      "It only works inside its window — the two prices form a band, and any move that clears both happens with no order against it, leaving the position unprotected",
      "Two levels instead of one: get the stop-to-limit gap wrong and the order is too loose or too tight",
      "You are trading certainty of exit for certainty of price — a choice you have to make on purpose",
    ],
    misuses: [
      {
        title: "Setting the stop and limit almost on top of each other",
        tag: "No fill",
        why: "Squeeze the two prices together and there is almost no room for the market to trade between them — one quick candle can jump the whole band before your limit gets a chance.",
        instead:
          "Widen the gap between the stop and the limit so the band can survive a fast move.",
        example:
          "A 5-pip band from 1.08100 to 1.08050 is skipped in a single tick when price gaps from 1.08150 to 1.08000 — the limit never gets a chance to fill.",
      },
      {
        title: "Relying on it as crash protection",
        tag: "Gap risk",
        why: "The very scenario it is meant to guard — a violent gap — is the one where it is most likely to go unfilled. You end up unprotected exactly when protection matters most.",
        instead:
          "Know it may not exit in a crash; for catastrophic cover a plain stop at least guarantees an exit.",
        example:
          "The violent move you built the band to survive is the one that leaps both levels — leaving you in a losing position with no exit at all.",
      },
      {
        title: "Using a stop-limit to enter breakouts",
        tag: "Missed entry",
        why: "Breakouts move fast. Price can leap through the trigger and past your limit before the limit can fill, so the entry never happens and you watch the move from the side.",
        instead:
          "For breakout entries a plain stop is more likely to get you in — accept the small slippage.",
        example:
          "A buy stop-limit on a breakout gaps straight past the limit and the entry is missed — price then runs 90 pips, all of it without you.",
      },
    ],
    whenToUse: [
      "You want slippage protection on a stop-loss",
      "You are trading an orderly market, where your limit is likely to be reached",
      "You are willing to accept fill risk in exchange for price control",
    ],
    whenNotToUse: [
      "Getting out is the priority — if an unfilled position would be worse than a slightly worse fill, a plain stop guarantees the exit this one cannot",
      "You are opening a position, not protecting one — entering needs the fill to happen, and this order's whole trade-off is that it might not",
      "You want a set-and-forget exit — once triggered it becomes a live limit to re-check or cancel, so it never runs as hands-off as a plain stop",
    ],
    related: ["stop-orders", "limit-orders"],
    cta: {
      title: "Two prices, and the gap between them is the risk",
      description:
        "The stop wakes the order and the limit caps it — any move that clears both leaves you holding nothing at all. See how thin liquidity turns a protected exit into an open position.",
      buttonLabel: "See what a fast market does to the band",
    },
    faqs: [
      {
        q: "When should I use a stop-limit instead of a stop?",
        a: "When the worst price you accept matters more than a guaranteed exit. A stop-limit caps your slippage — but in a fast move it may not fill at all, leaving the position open.",
      },
      {
        q: "What is the gap between the stop and the limit?",
        a: "The stop decides when the order activates; the limit sets the worst price you will accept. A narrow gap fills more easily but allows less slippage; a wide gap protects price harder but is easier for a fast market to skip.",
      },
      {
        q: "Can a stop-limit leave me without a stop-loss at all?",
        a: "Yes — that is its main risk. If price jumps past your limit, the order is never filled and the position keeps running unprotected. Many traders accept this to avoid a bad fill; others prefer a plain stop.",
      },
    ],
  },
];

export const comparisonRows = [
  {
    name: "Market",
    slug: "market-orders",
    execution: "Immediate",
    priceControl: "None — next best price",
    bestFor: "Fast entry or exit",
    risk: "Slippage",
  },
  {
    name: "Limit",
    slug: "limit-orders",
    execution: "When price reaches your level",
    priceControl: "Full — your price or better",
    bestFor: "Buying dips, selling rallies",
    risk: "No fill",
  },
  {
    name: "Stop",
    slug: "stop-orders",
    execution: "On trigger, then market",
    priceControl: "Trigger only — fill at market",
    bestFor: "Stop-losses, breakout entries",
    risk: "Slippage",
  },
  {
    name: "Stop-Limit",
    slug: "stop-limit-orders",
    execution: "On trigger, then limit",
    priceControl: "Trigger and limit band",
    bestFor: "Protected stop-losses",
    risk: "No fill",
  },
];

export const orderTypeTakeaways = [
  {
    key: "Market",
    slug: "market-orders",
    icon: "ri-rocket-2-line",
    gain: "You buy an instant fill",
    cost: "you pay in slippage",
  },
  {
    key: "Limit",
    slug: "limit-orders",
    icon: "ri-filter-3-line",
    gain: "You buy your exact price",
    cost: "you pay in fill risk",
  },
  {
    key: "Stop",
    slug: "stop-orders",
    icon: "ri-alarm-warning-line",
    gain: "You buy an automatic exit",
    cost: "you pay in slippage",
  },
  {
    key: "Stop-Limit",
    slug: "stop-limit-orders",
    icon: "ri-shield-keyhole-line",
    gain: "You buy a price ceiling",
    cost: "you pay in fill risk",
  },
];

export const takeawayRule =
  "Read the four together and the pattern is plain: every order buys you one certainty and charges you in another. That trade-off is the whole story.";

export const extendedTypes = [
  {
    name: "Trailing Stop",
    icon: "ri-line-chart-line",
    builtOn: "Stop",
    text: "A stop that follows price as it moves in your favour, locking in profit while staying a fixed distance behind it. As the trade runs, the stop ratchets up — but it never moves back down.",
    use: "Trend trades you plan to ride without naming a fixed target.",
  },
  {
    name: "OCO (One-Cancels-Other)",
    icon: "ri-forbid-2-line",
    builtOn: "Stop and Limit",
    text: "Two linked orders held as a pair — the moment one fills, the other is cancelled automatically. It keeps you from being filled twice on the same idea.",
    use: "Breakout setups where either direction could trigger first.",
  },
  {
    name: "Take-Profit",
    icon: "ri-trophy-line",
    builtOn: "Limit",
    text: "Usually a limit order attached to an open trade that closes it automatically once a profit target is reached — so a winner is banked without you watching.",
    use: "Any trade with a clear level where you want out.",
  },
  {
    name: "Fill or Kill",
    icon: "ri-timer-flash-line",
    builtOn: "Market",
    text: "An order that must be filled immediately and in full, or it is cancelled outright. Nothing is left resting in the book waiting around.",
    use: "Large orders where a partial fill would be a problem.",
  },
];